/**
 * Direction, and the shape of getting there.
 *
 * No platform reports "followers a week ago", so the only history that exists
 * is the history the collector keeps — one reading per day. That has two
 * consequences worth being honest about on screen rather than hiding:
 *
 * A newly connected channel has a number and no direction, and saying "day 3 of
 * 7" is more useful than a flat arrow that looks like stagnation.
 *
 * And the comparison is against the oldest reading we hold, not a fixed seven
 * days, so the label states the span it actually measured. A three-day change
 * captioned "this week" would be wrong in whichever direction flattered us.
 */
import './Trend.css'

const DAY = 24 * 60 * 60 * 1000

export function Trend({ samples }: { samples: { at: number; followers: number }[] }) {
  if (samples.length < 2) {
    return (
      <div className="trend trend--waiting">
        Trend starts once there are two days of readings — this is day{' '}
        {Math.max(1, samples.length)}.
      </div>
    )
  }

  const first = samples[0]!
  const last = samples[samples.length - 1]!
  const delta = last.followers - first.followers
  const days = Math.max(1, Math.round((last.at - first.at) / DAY))
  const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→'

  // A flat line has no range, and dividing by zero would collapse the path to a
  // single edge. Held at the vertical middle instead.
  const values = samples.map((s) => s.followers)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min
  const W = 88
  const H = 22
  const points = samples
    .map((s, i) => {
      const x = (i / (samples.length - 1)) * W
      const y = span === 0 ? H / 2 : H - ((s.followers - min) / span) * H
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <div className="trend">
      <svg
        className={`trend__spark trend__spark--${dir}`}
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${days}-day trend: ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta)}`}
      >
        <polyline points={points} fill="none" strokeWidth="1.5" />
      </svg>
      <span className={`trend__delta trend__delta--${dir}`}>
        {arrow} {delta === 0 ? 'no change' : `${delta > 0 ? '+' : '−'}${Math.abs(delta)}`}
      </span>
      <span className="trend__span">over {days === 1 ? '1 day' : `${days} days`}</span>
    </div>
  )
}
